import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import { Tableau, Participation, Matchs, Tour } from "../../../types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PlayCircleIcon from "@mui/icons-material/PlayCircleFilled";
import GroupIcon from "@mui/icons-material/Group";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

export interface TableauProgressBlockProps {
  tableau: Tableau;
  participations: Participation[];
  matchs: Matchs[];
  tours: Tour[];
}

// Calcule le nombre de poules et leur répartition
function computePoules(nbPointes: number): {
  nbPoules: number;
  nbPoules3: number;
  nbPoules2: number;
  poules: Array<{ nbJoueurs: number; matchs: number }>;
} {
  if (nbPointes <= 0)
    return { nbPoules: 0, nbPoules3: 0, nbPoules2: 0, poules: [] };
  if (nbPointes === 2)
    return {
      nbPoules: 1,
      nbPoules3: 0,
      nbPoules2: 1,
      poules: [{ nbJoueurs: 2, matchs: 1 }],
    };

  // On essaie de maximiser le nombre de poules de 3
  const nbPoules3 = Math.floor(nbPointes / 3);
  const reste = nbPointes % 3;

  // Si on a 1 joueur en trop, on transforme une poule de 3 en deux poules de 2
  let nbPoules2 = 0;
  if (reste === 1 && nbPoules3 > 0) {
    nbPoules2 = 2; // Une poule de 3 devient deux poules de 2
  } else {
    nbPoules2 = reste; // 0 ou 2 joueurs en trop
  }

  const poules = [
    ...Array(nbPoules3).fill({ nbJoueurs: 3, matchs: 3 }),
    ...Array(nbPoules2).fill({ nbJoueurs: 2, matchs: 1 }),
  ];

  return {
    nbPoules: nbPoules3 + nbPoules2,
    nbPoules3,
    nbPoules2,
    poules,
  };
}

// Fonction utilitaire pour déduire le nombre de joueurs requis pour chaque tour
function getNbPlayersForTour(tour: Tour) {
  const phase = (tour.phase || tour.label || "").toLowerCase();
  if (phase.includes("quart")) return 8;
  if (/(^|\s|-)8e/.test(phase)) return 16; // match '8e' mais pas 'quart'
  if (phase.includes("64")) return 64;
  if (phase.includes("32")) return 32;
  if (phase.includes("16")) return 16;
  if (phase.includes("demi")) return 4;
  if (phase.includes("finale")) return 2;
  return 0;
}

const TableauProgressBlock: React.FC<TableauProgressBlockProps> = ({
  tableau,
  participations,
  matchs,
  tours,
}) => {
  const nbMax = tableau.nbPlace || "-";
  const nbInscrits = participations.length;
  const nbPointes = participations.filter((p) => p.checkedIn).length;

  // Phase de poules
  const { nbPoules, nbPoules3, nbPoules2, poules } = computePoules(nbPointes);

  // On considère que les matchs de poules sont ceux du premier tour (tour label = 'Poules' ou similaire)
  const pouleMatchs = matchs.filter((m) => m.tour?.type === "poules");

  // Calcul du nombre de matchs terminés par poule
  const matchsParPoule = poules.map((poule) => {
    const matchsPoule = pouleMatchs.slice(0, poule.matchs);
    const termines = matchsPoule.filter((m) => m.status === "TERMINE").length;
    return { ...poule, termines };
  });

  // Une poule est terminée si tous ses matchs sont terminés
  const poulesTerminees = matchsParPoule.filter(
    (p) => p.termines === p.matchs
  ).length;
  const totalMatchsPoules = poules.reduce((sum, p) => sum + p.matchs, 0);
  const matchsPoulesTermines = pouleMatchs.filter(
    (m) => m.status === "TERMINE"
  ).length;

  // Phase à élimination directe
  let nbQualifies = 0;
  if (nbPointes > 0 && nbPoules > 0) {
    nbQualifies = nbPoules * 2;
  }
  let nbExempts = 0;
  if (nbQualifies > 2) {
    nbExempts = Math.max(0, 8 - nbQualifies);
  }
  const nbJoueursPhaseElim = nbQualifies;

  // Trie les tours d'élimination par ordre croissant (ordre du tournoi)
  const toursElimSorted = tours
    .filter((t) => t.type === "elim")
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

  // Trouve le tour de départ (ex: quart) dans cette liste
  const startTour =
    [...toursElimSorted]
      .sort((a, b) => getNbPlayersForTour(a) - getNbPlayersForTour(b))
      .find((t) => getNbPlayersForTour(t) >= nbJoueursPhaseElim) ||
    toursElimSorted[0];
  const startIdx = toursElimSorted.findIndex((t) => t.id === startTour.id);

  // Construction séquentielle des tours nécessaires dans l'ordre du tournoi
  const toursElim: {
    label: string;
    total: number;
    termines: number;
    enAttente: number;
    enCours: number;
    ordre: number;
  }[] = [];
  let joueursRestants = nbQualifies - nbExempts;
  let exemptsRestants = nbExempts;
  for (let i = startIdx; i < toursElimSorted.length; i++) {
    const tour = toursElimSorted[i];
    const tourMatchs = matchs.filter((m) => m.tourId === tour.id);
    const termines = tourMatchs.filter((m) => m.status === "TERMINE").length;
    const enAttente = tourMatchs.filter(
      (m) => m.status === "CREE" || m.status === "LIBERE"
    ).length;
    const enCours = tourMatchs.filter((m) => m.status === "ENCOURS").length;
    let total = Math.floor(joueursRestants / 2);
    if (total <= 0 && i !== toursElimSorted.length - 1) break;
    toursElim.push({
      label: tour.label,
      total,
      termines,
      enAttente,
      enCours,
      ordre: tour.ordre ?? 0,
    });
    if (i === startIdx) {
      joueursRestants = total + exemptsRestants;
      exemptsRestants = 0;
    } else {
      joueursRestants = total;
    }
    if (total === 1) break;
  }
  const toursElimFiltres = toursElim;

  const labelPremierTour =
    toursElimFiltres.length > 0 ? toursElimFiltres[0].label : "1er tour";

  // Statut des poules
  let statusPoules: "a_venir" | "en_cours" | "termine" = "a_venir";
  if (poulesTerminees === nbPoules && nbPoules > 0) {
    statusPoules = "termine";
  } else if (matchsPoulesTermines > 0) {
    statusPoules = "en_cours";
  }

  // Statut de chaque tour éliminatoire
  const toursElimFiltresWithStatus = toursElimFiltres.map((tour) => {
    let status: "a_venir" | "en_cours" | "termine" = "a_venir";
    if (tour.termines === tour.total && tour.total > 0) {
      status = "termine";
    } else if (tour.termines > 0 || tour.enCours > 0) {
      status = "en_cours";
    }
    return { ...tour, status };
  });

  // Statut global du tableau
  let statusGlobal: "a_venir" | "en_cours" | "termine" = "a_venir";
  if (
    statusPoules === "termine" &&
    toursElimFiltresWithStatus.every((t) => t.status === "termine") &&
    toursElimFiltresWithStatus.length > 0
  ) {
    statusGlobal = "termine";
  } else if (
    statusPoules === "en_cours" ||
    toursElimFiltresWithStatus.some(
      (t) => t.status === "en_cours" || t.status === "termine"
    )
  ) {
    statusGlobal = "en_cours";
  }

  // Couleurs de fond selon le statut (très légères)
  const bgByStatus = {
    a_venir: "#fff", // blanc pur
    en_cours: "#fffbe6", // beige très clair
    termine: "#e6f9ec", // vert très clair
  };

  // Fond global du tableau : blanc si pas tout terminé, vert si tout terminé
  const bgGlobal = statusGlobal === "termine" ? bgByStatus.termine : "#fff";

  return (
    <Card
      sx={{
        mb: 2,
        p: 1,
        borderRadius: 3,
        boxShadow: 3,
        bgcolor: bgGlobal,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: "wrap", mb: 1 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
            {tableau.name} - {tableau.label}
          </Typography>
          <Chip
            icon={<EmojiEventsIcon color="warning" />}
            label={`Max : ${nbMax}`}
            color="warning"
            size="small"
            sx={{ fontWeight: 500 }}
          />
          <Chip
            icon={<GroupIcon color="info" />}
            label={`Inscrits : ${nbInscrits}`}
            color="info"
            size="small"
            sx={{ fontWeight: 500 }}
          />
          <Chip
            icon={<HowToRegIcon color="success" />}
            label={`Pointés : ${nbPointes}`}
            color="success"
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Stack>
        <Typography
          variant="subtitle2"
          color="textSecondary"
          gutterBottom
          sx={{ mb: 1 }}
        >
          Heure limite de pointage : {tableau.heureLimite}
        </Typography>
        <Divider sx={{ my: 1 }} />
        {/* PHASE DE POULES avec fond coloré */}
        <Box
          sx={{
            bgcolor: bgByStatus[statusPoules],
            borderRadius: 2,
            p: 1,
            mb: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: "wrap", mb: 1 }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
              Phase de poules
            </Typography>
            <Chip label={`${nbPoules} poules`} color="primary" size="small" />
            <Chip
              label={`${nbPoules3} × 3 joueurs`}
              color="info"
              size="small"
            />
            <Chip
              label={`${nbPoules2} × 2 joueurs`}
              color="info"
              size="small"
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flexWrap: "wrap", mb: 1 }}
          >
            <Chip
              icon={<CheckCircleIcon color="success" />}
              label={`Poules terminées : ${poulesTerminees} / ${nbPoules}`}
              color="success"
              size="small"
            />
            <Chip
              icon={<HourglassEmptyIcon color="warning" />}
              label={`Matchs à jouer : ${totalMatchsPoules}`}
              color="warning"
              size="small"
            />
          </Stack>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: "wrap", mb: 1 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 1 }}>
            Phase à élimination directe
          </Typography>
          <Chip
            icon={<GroupIcon color="info" />}
            label={`Sortis de poules : ${nbQualifies}`}
            color="info"
            size="small"
          />
          <Chip
            icon={<HowToRegIcon color="warning" />}
            label={`Exempts ${labelPremierTour} : ${nbExempts}`}
            color="warning"
            size="small"
          />
        </Stack>
        <Box sx={{ mt: 1 }}>
          {toursElimFiltresWithStatus.map((tour, idx) => {
            const allDone = tour.termines === tour.total && tour.total > 0;
            return (
              <Box
                key={idx}
                sx={{
                  mb: 1,
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: 2,
                  bgcolor: bgByStatus[tour.status],
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                  sx={{ mb: 0.5 }}
                >
                  {tour.label}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5, flexWrap: "wrap" }}
                >
                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          color: allDone ? "success.main" : "warning.main",
                        }}
                      />
                    }
                    label={`Terminés : ${tour.termines} / ${tour.total}`}
                    color={allDone ? "success" : "warning"}
                    sx={{ fontWeight: "bold", fontSize: 16, px: 1.5 }}
                  />
                  <Chip
                    icon={<HourglassEmptyIcon color="warning" />}
                    label={`À lancer : ${tour.enAttente}`}
                    color="warning"
                    variant="outlined"
                    sx={{ fontSize: 14 }}
                  />
                  <Chip
                    icon={<PlayCircleIcon color="info" />}
                    label={`En cours : ${tour.enCours}`}
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: 14 }}
                  />
                </Stack>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TableauProgressBlock;
